var m = require('mithril');
var prop = require('mithril/stream');
var moment = require('moment');
var equipmentModel = require('../models/equipment');
var downloadReportModalInterface = require('../../layout/components/downloadReportModalInterface');
var loaders = require('../../layout/components/loaders');
var rowActionDropdown = require('../../components/rowActionDropdown');
var reportsMenu = {};
var roomClassTreeCache = null;
var roomClassTreePromise = null;

function loadRoomClassTree() {
  if (roomClassTreeCache) return Promise.resolve(roomClassTreeCache);
  if (roomClassTreePromise) return roomClassTreePromise;
  roomClassTreePromise = loaders.requestWithLoader({
    url: '/api/v1/ontology/classtree?type=room',
    method: 'GET',
    background: true
  })
  .then(tree => {
    roomClassTreeCache = tree || { categories: [] };
    return roomClassTreeCache;
  })
  .catch(error => {
    console.error('Error loading room class tree:', error);
    roomClassTreeCache = { categories: [] };
    return roomClassTreeCache;
  })
  .finally(() => {
    roomClassTreePromise = null;
  });
  return roomClassTreePromise;
}

function humanizeClassName(name) {
  if (!name) return '';
  return name.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
}

function getRecordClassName(record) {
  if (!record) return null;
  return record['@class'] || record.class || null;
}

function getDisplayClassName(records, fallbackClassName) {
  var classNames = Array.from(new Set((records || []).map(function (record) {
    return getRecordClassName(record);
  }).filter(Boolean)));
  if (classNames.length === 1) {
    return classNames[0];
  }
  return fallbackClassName || null;
}

function titleCase(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function deriveLegacyReportLabel(reportName) {
  var parts = String(reportName || '').split('-');
  if (parts.length < 2) {
    return humanizeClassName(reportName);
  }
  return parts[0].replace(/([A-Z])/g, ' $1').trim() + ' - ' +
    parts[1].replace(/([A-Z])/g, ' $1').charAt(0).toUpperCase() +
    parts[1].replace(/([A-Z])/g, ' $1').slice(1);
}

function buildReportMenuLabel(report) {
  if (report && report.label) {
    return report.label;
  }
  return deriveLegacyReportLabel(report && report.name);
}

function normalizeRidValue(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    return value.charAt(0) === '#' ? value.slice(1) : value;
  }
  if (value.rid) {
    return normalizeRidValue(value.rid);
  }
  if (value['@rid']) {
    return normalizeRidValue(value['@rid']);
  }
  return null;
}

function findPrimaryEquipmentTarget(records, targetClassName) {
  var normalizedRecords = Array.isArray(records) ? records.filter(Boolean) : [];
  if (!normalizedRecords.length) return null;
  var classMatch = normalizedRecords.find(function (record) {
    return getRecordClassName(record) === targetClassName;
  });
  return classMatch || normalizedRecords[0] || null;
}

function getPrimaryEquipmentTargetRid(records, targetClassName, locationRecord) {
  var primaryRecord = findPrimaryEquipmentTarget(records, targetClassName);
  var rid = normalizeRidValue(primaryRecord);
  if (rid) return rid;
  if (locationRecord && getRecordClassName(locationRecord) === targetClassName) {
    return normalizeRidValue(locationRecord);
  }
  return null;
}

function getManifestEntrypoints(manifestEntry) {
  if (manifestEntry && Array.isArray(manifestEntry.entrypoints) && manifestEntry.entrypoints.length > 0) {
    return manifestEntry.entrypoints;
  }
  return ['equipment'];
}

function manifestSupportsEntrypoint(manifestEntry, entrypoint) {
  return getManifestEntrypoints(manifestEntry).indexOf(entrypoint) !== -1;
}

reportsMenu.oninit = function (vnode) {
  var ctrl = this;
  ctrl.availableReports = [];
  ctrl.equipmentAllRids = prop(vnode.attrs.equipmentAllRids());
  ctrl.location = vnode.attrs.location();
  ctrl.mode = vnode.attrs.mode;
  let today = new Date();
  let monthAgo = new Date();
  monthAgo.setMonth(today.getMonth() - 1);
  ctrl.reportStartDateAndEndDate = prop([moment(monthAgo), moment(today)]);

  findEquipmentReportsAvailable(ctrl.availableReports, ctrl.equipmentAllRids);
  loadRoomClassTree()
    .then(() => {
      findEquipmentReportsAvailable(ctrl.availableReports, ctrl.equipmentAllRids);
      m.redraw();
    })
    .catch(() => {});
}

reportsMenu.onupdate = function(vnode) {
  var ctrl = this;
  if (
    ctrl.location['@rid'] !== vnode.attrs.location()['@rid'] ||
    ctrl.equipmentAllRids().length !== vnode.attrs.equipmentAllRids().length
  ) {
    ctrl.availableReports = [];
    ctrl.equipmentAllRids = prop(vnode.attrs.equipmentAllRids());
    ctrl.location = vnode.attrs.location();
    findEquipmentReportsAvailable(ctrl.availableReports, ctrl.equipmentAllRids);
  }
}

reportsMenu.view = function (vnode) {
  var ctrl = this;
  if (ctrl.availableReports.length === 0) {
    return;
  }
  return m('div.f4', [
    m(rowActionDropdown, {
      triggerLabel: 'Reports',
      align: 'left',
      buttonClass: 'dib b--blue br2 h2 flex items-center',
      buttonStyle: {
        minWidth: 'auto',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        lineHeight: '1'
      },
      items: ctrl.availableReports.map(function (report) {
        return {
          label: buildReportMenuLabel(report),
          onclick: function () {
            openReportDownload(report, vnode, ctrl);
          }
        };
      })
    })
  ])
};

function openReportDownload(report, vnode, ctrl) {
  var baseType = report.targetClassName;
  var mode = report.reportMode;
  var equipmentRecords = vnode.attrs.equipmentAllRids();
  var equipmentTargetRid = getPrimaryEquipmentTargetRid(equipmentRecords, baseType, vnode.attrs.location());
  var locationName = vnode.attrs.location().name == 'root' ? 'Global' : vnode.attrs.location().name;
  var classLabel = humanizeClassName(getDisplayClassName(equipmentRecords, baseType) || baseType);

  if (mode === 'detail') {
    downloadReportModalInterface.editDialogue({
      modalTitle: `Download ${classLabel} Detail Report`,
      reportType: report.reportType,
      reportMode: mode,
      targetKind: 'equipment',
      targetRid: equipmentTargetRid,
      targetClassName: baseType,
      chartDate: vnode.attrs.chartDate,
      span: vnode.attrs.span,
      resampling: vnode.attrs.resampling,
    });
    return;
  }

  if (mode === 'calibration') {
    downloadReportModalInterface.editDialogue({
      modalTitle: `Download ${locationName} ${classLabel} ${titleCase(mode)} Report`,
      reportType: report.reportType,
      reportMode: mode,
      targetKind: 'location',
      targetRid: vnode.attrs.location()['@rid'].slice(1),
      targetClassName: baseType
    });
    return;
  }

  if (mode === 'temperature') {
    downloadReportModalInterface.editDialogue({
      modalTitle: `Download temperature Report`,
      reportType: report.reportType,
      reportMode: mode,
      targetKind: 'equipment',
      targetRid: equipmentTargetRid,
      targetClassName: baseType,
      chartDate: vnode.attrs.chartDate,
      span: vnode.attrs.span,
      resampling: vnode.attrs.resampling
    });
    return;
  }

  if (mode === 'compliance' || mode === 'live' || mode === 'snapshot') {
    var options = {
      modalTitle: `Download ${locationName} ${classLabel} ${titleCase(mode)} Report`,
      reportType: report.reportType,
      reportMode: mode,
      targetKind: 'location',
      targetRid: vnode.attrs.location()['@rid'].slice(1),
      targetClassName: baseType
    };
    if (mode !== 'live') {
      options.dates = ctrl.reportStartDateAndEndDate();
    }
    downloadReportModalInterface.editDialogue(options);
    return;
  }

  if (report.dataScope === 'location') {
    downloadReportModalInterface.editDialogue({
      modalTitle: report.label
        ? `Download ${locationName} ${report.label}`
        : `Download ${locationName} ${classLabel} ${titleCase(mode)} Report`,
      reportType: report.reportType,
      reportMode: mode,
      targetKind: 'location',
      targetRid: vnode.attrs.location()['@rid'].slice(1),
      targetClassName: baseType
    });
  }
}

function findEquipmentReportsAvailable(availableReports, equipmentAllRids) {
  if (equipmentAllRids().length === 0) {
    availableReports = [];
  }
  const categories = Array.isArray(equipmentModel.equipmentClassTree.categories)
    ? equipmentModel.equipmentClassTree.categories
    : [];
  const roomCategories = roomClassTreeCache && Array.isArray(roomClassTreeCache.categories)
    ? roomClassTreeCache.categories
    : [];
  const classSet = new Set();
  collectCategoryNames(categories, classSet);
  collectCategoryNames(roomCategories, classSet);
  const reportableRids = (equipmentAllRids() || []).filter(r => classSet.has(getRecordClassName(r)));
  const multipleRecordReport = reportableRids.length > 1;

  categories.forEach(category => {
    processCategory(category, availableReports, reportableRids, multipleRecordReport, []);
  });
  roomCategories.forEach(category => {
    processCategory(category, availableReports, reportableRids, multipleRecordReport, []);
  });
}

function collectCategoryNames(categoryNodes, classSet) {
  if (!Array.isArray(categoryNodes)) return;
  categoryNodes.forEach(node => {
    if (!node || !node.name) return;
    classSet.add(node.name);
    if (Array.isArray(node.children) && node.children.length > 0) {
      collectCategoryNames(node.children, classSet);
    }
  });
}

function processCategory(category, availableReports, equipmentAllRids, multipleRecordReport, inheritedReports) {
  let currentCategoryReports = inheritedReports.slice();
  if (category.reportManifest) {
    category.reportManifest.forEach(rm => {
      if (manifestSupportsEntrypoint(rm, 'equipment')) {
        currentCategoryReports.push({originCategory: category, rm});
      }
    });
  }
  if (equipmentAllRids.some(r => getRecordClassName(r) === category.name)) {
    if (inheritedReports.length > 0) {
      inheritedReports.forEach(({originCategory, rm}) => {
        const reportName = `${category.name}-${rm.name}`;
        const report = {
          name: reportName,
          equipmenClass: category.name,
          reportType: `${rm.templateName || originCategory.name}:${rm.name}`,
          reportMode: rm.name,
          targetClassName: (rm.dataScope === 'location' && rm.locationTraversalClass) ? rm.locationTraversalClass : category.name,
          dataScope: rm.dataScope || null,
          label: rm.ui && rm.ui.createLabel ? rm.ui.createLabel : null
        };
        if (
          ((multipleRecordReport && rm.multipleRecord) || 
          (!multipleRecordReport && !rm.multipleRecord)) &&
          !availableReports.some(r => r.name === reportName)
        ) {
          availableReports.push(report);
        }
      });
    }
    if (category.reportManifest) {
      category.reportManifest.forEach(rm => {
        if (!manifestSupportsEntrypoint(rm, 'equipment')) {
          return;
        }
        const reportName = `${category.name}-${rm.name}`;
        const report = {
          name: reportName,
          equipmenClass: category.name,
          reportType: `${rm.templateName || category.name}:${rm.name}`,
          reportMode: rm.name,
          targetClassName: (rm.dataScope === 'location' && rm.locationTraversalClass) ? rm.locationTraversalClass : category.name,
          dataScope: rm.dataScope || null,
          label: rm.ui && rm.ui.createLabel ? rm.ui.createLabel : null
        };
        if (
          ((multipleRecordReport && rm.multipleRecord) || 
          (!multipleRecordReport && !rm.multipleRecord)) &&
          !availableReports.some(r => r.name === reportName)
        ) {
          availableReports.push(report);
        }
      });
    }
  }

  if (Array.isArray(category.children) && category.children.length > 0) {
    category.children.forEach(childCategory => {
      processCategory(childCategory, availableReports, equipmentAllRids, multipleRecordReport, currentCategoryReports);
    });
  }
}




module.exports = reportsMenu;
module.exports._private = {
  getDisplayClassName: getDisplayClassName,
  getPrimaryEquipmentTargetRid: getPrimaryEquipmentTargetRid,
  getRecordClassName: getRecordClassName,
  normalizeRidValue: normalizeRidValue,
  findEquipmentReportsAvailable: findEquipmentReportsAvailable
};
